/**
 * The fleet view: one row per repo with its score and trend, the rules it is held to, the
 * ratchet, its worst rules, waivers about to run out and what the drift scan found. Pure.
 */
import { disciplineRuleInfo } from '@orgus/protocol';
import type { DisciplineDriftReport, DisciplineState, Id } from '@orgus/protocol';
import { waiverStatus } from './feed.ts';
import type { RepoRow } from './model.ts';

export interface FleetRow extends RepoRow {
  /** Pack names, short ('strict', 'typescript', 'acme/web'). */
  packs: string[];
  rulebookFrom: 'repo' | 'hub' | 'recommended' | null;
  topRules: { rule: string; title: string; count: number }[];
  waiversActive: number;
  waiversExpiring: number;
  drift: DisciplineDriftReport | null;
  driftTotal: number;
  /** New violations, debt left and debt burned, when the repo has a ratchet. */
  ratchet: { fresh: number; remaining: number; total: number; burned: number } | null;
}

export const shortPack = (p: string) => p.replace(/^orgus\//, '').replace(/@[\d.]+$/, '');

export function fleetRows(rows: readonly RepoRow[], state: DisciplineState | undefined, now: number): FleetRow[] {
  const driftBy = new Map<Id, DisciplineDriftReport>((state?.drift ?? []).map((d) => [d.projectId, d]));
  return rows.map((row) => {
    const r = row.report;
    const packs = (r?.packs ?? (r?.pack ? [`orgus/${r.pack}`] : [])).map(shortPack).filter((p) => p !== 'recommended');
    const topRules = (r?.rules ?? [])
      .filter((s) => s.errors + s.warns > 0)
      .sort((a, b) => b.errors - a.errors || b.warns - a.warns)
      .slice(0, 2)
      .map((s) => ({ rule: s.rule, title: disciplineRuleInfo(s.rule).title, count: s.errors + s.warns }));
    const ws = (state?.waivers ?? []).filter((w) => w.projectId === row.projectId).map((w) => waiverStatus(w, now));
    const drift = driftBy.get(row.projectId) ?? null;
    const b = state?.baselines?.[row.projectId];
    return {
      ...row,
      packs: packs.length ? packs : r ? ['recommended'] : [],
      rulebookFrom: r ? r.rulebook : null,
      topRules,
      waiversActive: ws.filter((s) => s === 'active' || s === 'expiring').length,
      waiversExpiring: ws.filter((s) => s === 'expiring').length,
      drift,
      driftTotal: drift ? Object.values(drift.counts).reduce((a, x) => a + x, 0) : 0,
      ratchet: r?.ratchet ? { fresh: r.ratchet.fresh, remaining: r.ratchet.remaining, total: r.ratchet.total, burned: r.ratchet.burned } : b ? { fresh: 0, remaining: b.remaining, total: b.total, burned: b.total - b.remaining } : null,
    };
  });
}

/** Fleet-wide ratchet: debt left of the debt captured, and new violations. */
export function fleetRatchet(rows: readonly FleetRow[]): { repos: number; remaining: number; total: number; fresh: number } | null {
  const withR = rows.filter((r) => r.ratchet);
  if (!withR.length) return null;
  return {
    repos: withR.length,
    remaining: withR.reduce((n, r) => n + r.ratchet!.remaining, 0),
    total: withR.reduce((n, r) => n + r.ratchet!.total, 0),
    fresh: withR.reduce((n, r) => n + r.ratchet!.fresh, 0),
  };
}
